import axios from 'axios';
import { ApiError } from '../functions/getApiErrorMessage';
import { stripInvalidMbiChars } from '../functions/stripInvalidMbiChars';
import { DateObject } from '../objects/models/date.field.value';
import { defaultHeader } from '../hooks/useAxios';
import { useInflightTracker } from './useInflightTracker';

const formatDateOfBirth = (dobObject: DateObject) => {
    let {day, month, year} = dobObject;
    month = month.padStart(2, '0');
    day = day.padStart(2, '0');
    return [year, month, day].join('-');
};

const formatLastName = (lastName: string, suffix: string) => {
    return [lastName, suffix].join(' ').trim();
};

const formatSecretQuestionAnswer = (question: string, answer: string) => {
    return {
        challengeQuestionAndAnswers: [
            {
                question,
                answer,
            },
        ],
    };
};

interface Step1Info {
    mbi: string
    username: string
    lastName: string
    suffix: string
    dateOfBirth: DateObject
}

interface Step3Info extends Step1Info {
    secretQuestion: string
    secretAnswer: string
    password: string
}

export interface ForgotPasswordErrorsResponse {
    code?: number
    errors?: ApiError[]
}

export const useForgotPasswordApi = () => {
    const tracker = useInflightTracker();

    // Step 1
    const getSecretQuestion = async (userInfo: Step1Info) => {
        const url = '/account/api/reset-password/step/1';
        if (tracker.isAlreadyInflight(url))
            return;
        tracker.addApiCall(url);
        const {mbi, username, lastName, suffix, dateOfBirth} = userInfo;
        const data = {
            mbi: stripInvalidMbiChars(mbi),
            username,
            lastName: formatLastName(lastName, suffix),
            dateOfBirth: formatDateOfBirth(dateOfBirth)
        };
        try {
            const res = await axios.post(url, data, { headers: defaultHeader });
            tracker.removeApiCall(url);
            return res.data.challengeQuestion;
        } catch (e: any) {
            tracker.removeApiCall(url);
            if (e.response) {
                throw e.response.data; // throw error response from SLS
            } else {
                throw e;
            }
        }
    };

    // Step 2
    const answerSecretQuestion = async (mbi: string, question: string, answer: string) => {
        const url = '/account/api/reset-password/step/2';
        if (tracker.isAlreadyInflight(url))
            return;
        tracker.addApiCall(url);
        const data = {
            mbi: stripInvalidMbiChars(mbi),
            ...formatSecretQuestionAnswer(question, answer)
        };

        try {
            await axios.post(url, data, { headers: defaultHeader });
            tracker.removeApiCall(url);
        } catch (e: any) {
            tracker.removeApiCall(url);
            if (e.response) {
                throw e.response.data; // throw error response from SLS
            } else {
                throw e;
            }
        }

    };

    // Step 3
    const resetPassword = async (resetPasswordInfo: Step3Info) => {
        const url = '/account/api/reset-password/step/3';
        if (tracker.isAlreadyInflight(url))
            return;
        tracker.addApiCall(url);
        const {
            mbi,
            username,
            lastName,
            suffix,
            dateOfBirth,
            secretQuestion,
            secretAnswer,
            password
        } = resetPasswordInfo;

        const data = {
            mbi: stripInvalidMbiChars(mbi),
            username,
            lastName: formatLastName(lastName, suffix),
            dateOfBirth: formatDateOfBirth(dateOfBirth),
            newPassword: password,

            ...formatSecretQuestionAnswer(secretQuestion, secretAnswer)
        };

        try {
            await axios.post(url, data, { headers: defaultHeader });
            tracker.removeApiCall(url);
        } catch (e: any) {
            tracker.removeApiCall(url);
            if (e.response) {
                throw e.response.data; // throw error response from SLS
            } else {
                throw e;
            }
        }

    };

    return {
        getSecretQuestion,
        answerSecretQuestion,
        resetPassword
    };
}
